import {
  cloneElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from 'messages';
import List from 'components/list/List';
import ListBody from 'components/list/ListBody';
import { Card, CardBody } from 'components/card';
import Message from 'components/message/Message';
import MessageText from 'components/message/MessageText';
import { useUIDSeed } from 'react-uid';
import VirtualListItem from 'containers/partials/list-items/VirtualListItem';
import PersonStatusPopper from 'containers/partials/misc/PersonStatusPopper';
import ListItemLabel from 'components/list/list-item/ListItemLabel';
import { PersonStatus } from 'constants.js';

import { useVirtualizer } from '@tanstack/react-virtual';
import MetaWrapper from 'containers/partials/list-items/MetaWrapper';
import Badge from 'components/badge/Badge';
import Icon from 'components/icon/Icon';
import { Button, ButtonsGroup } from 'components/button';
import { MenuWrapper } from 'components/menu';
import FieldInput from 'components/input/FieldInput';
import { useStore } from 'stores/Store';

const VirtualizedEntityList = ({
  id,
  activeId,
  items = [],
  statusLabel,
  checkableGroups,
  checkable,
  enableSelectAll = true,
  showOnlyGroups,
  sortableListHeader,
  onClick,
  onFilter,
  disabledItems = [],
  excludedItems = [],
  extraClassNames = {},
  onModal,
  showPersonStatus,
  onClickPersonStatus,
  withFixedFooter,
  emptyMessage,
  reCalculateHeight = false,
  variableHeight = false,

  children
}) => {
  const intl = useIntl();

  const [filter, setFilter] = useState({ search: '' });

  const selected = useStore(state => state.selectedEntities);
  const selectedGroups = useStore(state => state.selectedGroups);
  const select = useStore(state => state.selectEntities);
  const selectGroup = useStore(state => state.selectGroups);
  const setSelected = useStore(state => state.setSelectedEntities);
  const clearSelected = useStore(state => state.clearSelectedEntities);
  const removeSelected = useStore(state => state.removeSelectedEntities);

  const parentRef = useRef();
  const inputSearchRef = useRef();
  const uidSeed = useUIDSeed();

  const [minHeight, setMinHeight] = useState(null);
  const [minHeightParent, setMinHeightParent] = useState(null);
  const [heightList, setHeightList] = useState(null);

  const [selectAllState, setSelectAllState] = useState(false);
  const [selectSomeState, setSelectSomeState] = useState(false);
  const [hasPersons, setHasPersons] = useState(false);
  const [filterActive, setFilterActive] = useState(false);

  const rowVirtualizer = useVirtualizer({
    count: items?.length ?? 0,
    getScrollElement: () => parentRef.current,
    estimateSize: useCallback(() => 56, []),
    overscan: 5
  });

  useEffect(() => {
    const activeItems = items.filter(
      el => !excludedItems.find(exI => exI.id === el.id)
    );
    if (selectSomeState) {
      if (selected.length > 0) {
        if (selected.length < activeItems.filter(el => el.filtered).length) {
          setSelectSomeState(false);
        }
        if (selected.length === activeItems.filter(el => el.filtered).length) {
          setSelectSomeState(true);
        }
      }
    }
    if (selectAllState) {
      if (selected.length < activeItems.length) {
        setSelectAllState(false);
      }
    }

    if (selected.length > 0) {
      if (selected.length >= activeItems.length) {
        // Check if all items are in the selected array
        setSelectAllState(
          activeItems.every(item => selected.find(s => s.id === item.id))
        );
      }
    }
  }, [selected, items]);

  useEffect(() => {
    setFilterActive(
      filter.search.length > 0 &&
        items.filter(
          el => el.filtered && !excludedItems.find(exI => exI.id === el.id)
        ).length > 0
    );
  }, [filter, items]);

  useEffect(() => {
    if (items) {
      setHasPersons(
        items.some(item => item.constructor.modelName === 'Person')
      );
    }
  }, [items]);

  const onSelectHandler = item => {
    if (excludedItems.find(exI => exI.id === item.id)) return;

    if (
      item.constructor.modelName === 'Person' ||
      item.constructor.modelName === 'User'
    ) {
      select([item]);
    } else if (item.constructor.modelName === 'Group') {
      selectGroup([item]);
    }
  };

  const checkAndSetHeight = theRef => {
    if (theRef?.current) {
      const roundedTop = theRef.current.getBoundingClientRect().top.toFixed(0);

      setMinHeight(`calc(100vh - 32px - ${roundedTop}px)`);
      setMinHeightParent(`calc(100vh - ${roundedTop}px)`);

      //   reCalculateHeight && reCalculate(roundedTop);
    }
  };

  const reCalculate = (top = false) => {
    if (top !== false) {
      setHeightList(`calc(100vh - ${top}px - 70px)`);
    } else {
      const roundedTop = parentRef?.current
        ?.getBoundingClientRect()
        .top.toFixed(0);

      const footerSpacing = document.querySelector(
        '.c-card__footer-bottom-fixed'
      )
        ? 70
        : 0;
      return `calc(100vh - ${roundedTop}px - ${footerSpacing}px)`;
    }
  };

  useLayoutEffect(() => {
    const interval = setInterval(() => {
      if (parentRef) {
        checkAndSetHeight(parentRef);
        variableHeight && reCalculate();
        clearInterval(interval);
      }
    }, 100);
  }, [parentRef, items]);

  useLayoutEffect(() => {
    const interval = setInterval(() => {
      if (inputSearchRef) {
        inputSearchRef?.current?.value?.length > 0
          ? inputSearchRef?.current
              ?.closest('.c-input--expandable')
              .classList.add('c-input--expanded')
          : inputSearchRef?.current
              ?.closest('.c-input--expandable')
              .classList.remove('c-input--expanded');
        clearInterval(interval);
      }
    }, 100);
  }, [filterActive]);

  if (!Array.isArray(items)) return null;

  const groupIsChecked = group => {
    return (
      selectedGroups.find(s => s.id === group.id) || // !checkableGroups && // mark as checked when all persons are selected, only when checkableGroups is false
      (group.clients.length > 0 &&
        group.clients.every(client =>
          new Set([
            ...selected.map(s => s.id),
            ...excludedItems.map(exI => exI.id)
          ]).has(client.id)
        ))
    );
  };

  const isIndeterminateChecked = group => {
    return (
      group.clients.some(
        client =>
          selected.find(p => p.id === client.id) ||
          excludedItems.find(exI => exI.id === client.id)
      ) &&
      !group.clients.every(client =>
        [
          ...selected.map(s => s.id),
          ...excludedItems.map(exI => exI.id)
        ].includes(client.id)
      )
    );
  };

  const personIsChecked = person => {
    return (
      selected.find(p => p.id === person.id) ||
      excludedItems.find(exI => exI.id === person.id)
    );
  };

  const onSearch = async e => {
    e.target.value.length > 0
      ? e.target
          .closest('.c-input--expandable')
          .classList.add('c-input--expanded')
      : e.target
          .closest('.c-input--expandable')
          .classList.remove('c-input--expanded');
    await setFilter({ search: e.target.value });

    onFilter && onFilter({ search: e.target.value });
  };

  const selectAll = () => {
    if (selectSomeState) {
      setSelectSomeState(!selectSomeState);
    }

    if (selectAllState) {
      clearSelected();
    } else {
      setSelected(
        items.filter(el => !excludedItems.find(exI => exI.id === el.id))
      );
    }
    setSelectAllState(!selectAllState);
  };

  const handleSelectFilteredItemsFromMenu = () => {
    const filtered = items.filter(
      el => el.filtered && !excludedItems.find(exI => exI.id === el.id)
    );
    if (selectAllState) {
      removeSelected(items);
      setSelectAllState(!selectAllState);
    }

    if (selectSomeState) {
      removeSelected(filtered);
    } else {
      setSelected(filtered);
    }
    setSelectSomeState(!selectSomeState);
  };

  const handleSelectAllItemsFromMenu = () => {
    selectAll();
  };

  const handleMainButton = () => {
    if (selectAllState) {
      selectAll();
    } else if (selectSomeState) {
      handleSelectFilteredItemsFromMenu();
    } else {
      filterActive ? handleSelectFilteredItemsFromMenu() : selectAll();
    }
  };

  const SelectPersonsCheckbox = () => {
    let label;
    let icon = 'checkbox-no';

    // make a list of all items that are not in the excludedItems list
    const activeItems = items.filter(
      el => !excludedItems.find(exI => exI.id === el.id)
    );

    if (selectAllState || selectSomeState) {
      label = intl.formatMessage(messages.clearSelection, {
        count:
          filterActive && selectSomeState
            ? activeItems.filter(el => el.filtered).length
            : activeItems.length
      });
      icon = 'checkbox-yes-blue';
    } else if (filterActive) {
      label = intl.formatMessage(messages.selectSomePersonsButton, {
        count: activeItems.filter(el => el.filtered).length
      });
    } else {
      label = intl.formatMessage(messages.selectAllButton, {
        count: activeItems.filter(el => el.filtered).length
      });
    }

    return (
      <Button
        halfRounded
        ghost
        onClick={handleMainButton}
        title={label}
        disabled={activeItems.filter(el => el.filtered).length <= 0}
      >
        <Icon id={icon} mediumIcon />
        <span className="c-button--ellipsed">{label}</span>
      </Button>
    );
  };

  if (items.length > 0) {
    return (
      <>
        <div className="p-tabs__tab-header">
          {enableSelectAll && hasPersons && (
            <div className="u-1-of-2 u-padding-right-tiny">
              <ButtonsGroup halfButtonsMerged>
                <SelectPersonsCheckbox />
                <MenuWrapper
                  trigger={
                    <Button halfRounded ghost>
                      <Icon id="dropdown-open" fillColor="color-neutral" />
                    </Button>
                  }
                >
                  <Button
                    disabled={!filterActive}
                    menu
                    onClick={handleSelectFilteredItemsFromMenu}
                  >
                    {selectSomeState ? (
                      <FormattedMessage
                        {...messages.clearSelection}
                        values={{
                          count: filterActive
                            ? items.filter(
                                el =>
                                  el.filtered &&
                                  !excludedItems.find(exI => exI.id === el.id)
                              ).length
                            : ''
                        }}
                      />
                    ) : (
                      <FormattedMessage
                        {...messages.selectSomePersonsButton}
                        values={{
                          count: filterActive
                            ? items.filter(
                                el =>
                                  el.filtered &&
                                  !excludedItems.find(exI => exI.id === el.id)
                              ).length
                            : ''
                        }}
                      />
                    )}

                    {selectSomeState && <Icon id="checked-all" />}
                  </Button>
                  <Button menu onClick={handleSelectAllItemsFromMenu}>
                    {selectAllState ? (
                      <FormattedMessage
                        {...messages.clearSelection}
                        values={{ count: items.length }}
                      />
                    ) : (
                      <FormattedMessage
                        {...messages.selectAllButton}
                        values={{ count: items.length }}
                      />
                    )}
                    {selectAllState && <Icon id="checked-all" />}
                  </Button>
                </MenuWrapper>
              </ButtonsGroup>
            </div>
          )}
          <div className="u-1-of-2 u-padding-left-tiny u-margin-left-auto">
            <FieldInput
              id={`${id}_search`}
              placeholder={intl.formatMessage(
                messages.searchUsersAndGroupsPlaceholder
              )}
              title={intl.formatMessage(
                messages.searchUsersAndGroupsPlaceholder
              )}
              value={filter.search}
              onChange={onSearch}
              type="text"
              rounded
              icon="search-grey"
              iconLeft
              useNative
              extraClassNames="u-margin-bottom-none c-input--expandable"
              ref={inputSearchRef}
            />
          </div>
        </div>
        {filter.search.length > 0 && (
          <MessageText extraClassNames="c-list__filtering-message">
            <FormattedMessage
              {...messages.searchInputFeedback}
              values={{
                count: items.filter(
                  el =>
                    el.filtered && !excludedItems.find(exI => exI.id === el.id)
                ).length,
                total: items.length,
                searchTerm: filter.search,
                //
                b: chunks => <b>{chunks}</b>
              }}
            />
          </MessageText>
        )}
        {sortableListHeader && sortableListHeader}
        <List
          ref={parentRef}
          virtualizedList
          onModal={onModal}
          style={{
            maxHeight: `${minHeightParent}`,
            height: withFixedFooter ? 'calc(100% - 64px)' : '100%'
          }}
          extraClassNames={extraClassNames}
        >
          <ListBody
            style={{
              minHeight: !onModal && !reCalculateHeight && minHeight,
              height: reCalculateHeight
                ? reCalculate()
                : rowVirtualizer.getTotalSize(),
              position: 'relative',
              marginBottom: 0
            }}
          >
            {rowVirtualizer.getVirtualItems().map(virtualRow => {
              const item = items[virtualRow.index];
              item.index = virtualRow.index;

              const disabled = disabledItems.find(exI => exI.id === item.id);
              const excluded = excludedItems.find(exI => exI.id === item.id);

              let listData;

              if (
                item.constructor.modelName === 'Person' ||
                item.constructor.modelName === 'User'
              ) {
                listData = {
                  titleLabel: 'fullName',
                  statusLabel:
                    statusLabel && typeof statusLabel === 'function'
                      ? statusLabel(item)
                      : statusLabel || item.namePrefix,
                  checkbox: checkable && !excluded,
                  onClick: () => onClick && onClick(item, 'person'),
                  checked: checkable && personIsChecked(item) && !excluded,
                  lastItem:
                    item.index === rowVirtualizer.getVirtualItems().length - 1,
                  meta:
                    (showPersonStatus && (
                      <MetaWrapper>
                        <PersonStatusIcon
                          item={item}
                          onClick={onClickPersonStatus}
                        />
                      </MetaWrapper>
                    )) ||
                    (excluded && (
                      <MetaWrapper>
                        <Badge tag>
                          <FormattedMessage {...messages.excludedEntityLabel} />
                        </Badge>
                      </MetaWrapper>
                    ))
                };
              } else if (item.constructor.modelName === 'Group') {
                listData = {
                  titleLabel: 'name',
                  checkbox: checkableGroups,
                  checked: groupIsChecked(item),
                  disabled: !checkableGroups,
                  indeterminate:
                    !checkableGroups && isIndeterminateChecked(item),
                  mediumIcon: false,
                  icon: 'group',
                  onClick: () =>
                    ((onClick &&
                      showOnlyGroups &&
                      item.subEntities.length > 0) ||
                      (onClick && !showOnlyGroups)) &&
                    onClick(item, 'group'),
                  meta: (
                    <MetaWrapper>
                      <p className="c-list__meta">
                        <FormattedMessage
                          {...messages.modalListItemSportersGroupsCount}
                          values={{
                            athletes:
                              (item.clients && item.clients.length) || 0,
                            groups: item.subEntities.length
                          }}
                        />
                      </p>
                    </MetaWrapper>
                  )
                };
              }

              if (showOnlyGroups && item.constructor.modelName === 'Person')
                return null;

              if (children) {
                const ChildrenItems = children(item);
                return cloneElement(ChildrenItems, {
                  uid: uidSeed(item.id),
                  key: virtualRow.index,
                  item: item,
                  virtualRow: virtualRow,
                  virtualRef: rowVirtualizer.measureElement,
                  onChange: e => onSelectHandler(item, e),
                  ...listData
                });
              }

              return (
                <VirtualListItem
                  uid={uidSeed(item.id)}
                  item={item}
                  key={virtualRow.index}
                  virtualRow={virtualRow}
                  active={item.id === activeId}
                  statusLabel={statusLabel}
                  clickable={!excluded && item.filtered}
                  outfaded={excluded || !item.filtered}
                  disabled={disabled}
                  filtered={item.filtered}
                  absolutePositionedListItem
                  onChange={e => onSelectHandler(item, e)}
                  filteringTerm={filter}
                  {...listData}
                />
              );
            })}
          </ListBody>
        </List>
      </>
    );
  }

  return (
    <Card centered>
      <CardBody empty>
        <Message emptyState={true} icon="cursor">
          <MessageText>
            <FormattedMessage
              {...(emptyMessage ? emptyMessage : messages.noAthletesOrGroups)}
            />
          </MessageText>
        </Message>
      </CardBody>
    </Card>
  );
};

export default VirtualizedEntityList;

const PersonStatusIcon = ({ item, onClick }) => {
  if (
    ![
      PersonStatus.EMAIL,
      PersonStatus.USER,
      PersonStatus.PENDING,
      PersonStatus.UNCONFIRMED
    ].includes(item.status)
  )
    return null;

  return (
    <ListItemLabel info middleColumn style={{ paddingRight: 24 }}>
      <PersonStatusPopper person={item} onClickStatus={onClick} />
    </ListItemLabel>
  );
};
